import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SyncBadge } from "@/components/common/SyncBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { COUNTRIES, LANGUAGES, dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const CONVERSATION_TAB_LABEL = "System prompt";

const PROMPT_VARIABLES = [
  { key: "firstname", token: "{{firstname}}", label: "First name" },
  { key: "lastname", token: "{{lastname}}", label: "Last name" },
  { key: "fullname", token: "{{fullname}}", label: "Full name" },
  { key: "company", token: "{{company}}", label: "Company" },
  { key: "phone", token: "{{phone}}", label: "Phone" },
  { key: "product", token: "{{product}}", label: "Product" },
  { key: "country", token: "{{country}}", label: "Country" },
  { key: "email", token: "{{email}}", label: "Email" },
];

function VariableMenu({ disabled, onInsert, testId }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled} data-testid={testId}>
          Insert variable
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white" align="end">
        {PROMPT_VARIABLES.map((item) => (
          <DropdownMenuItem
            key={item.key}
            onSelect={() => onInsert(item.token)}
            data-testid={`${testId}-${item.key}`}
          >
            <span className="font-mono text-xs">{item.token}</span>
            <span className="text-slate-500">{item.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AgentDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [compare, setCompare] = useState({ a: "", b: "" });
  const [diff, setDiff] = useState(null);
  const [newDoc, setNewDoc] = useState({ title: "", content: "" });
  const [livePhone, setLivePhone] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [assistantId, setAssistantId] = useState("");
  const firstMessageRef = useRef(null);
  const promptRef = useRef(null);

  const { data } = useQuery({ queryKey: ["agent", id], queryFn: () => get(`/agents/${id}`) });
  const { data: numbers } = useQuery({
    queryKey: ["phone-numbers", "agent-picker"],
    queryFn: () => get("/phone-numbers", { page_size: 200 }),
  });
  const { data: agentOwners } = useQuery({
    queryKey: ["agents", "number-owners"],
    queryFn: () => get("/agents", { page_size: 200 }),
  });
  const ownedElsewhere = new Set(
    (agentOwners?.items || []).filter((a) => a.id !== id).map((a) => a.phone_number_id).filter(Boolean)
  );
  const pickableNumbers = (Array.isArray(numbers) ? numbers : numbers?.items || []).filter(
    (n) => n.status === "active" && !ownedElsewhere.has(n.id)
  );
  const voiceProvider = form?.voice_provider || "vapi";
  const { data: voices } = useQuery({
    queryKey: ["voices", voiceProvider],
    queryFn: () => get("/voices", { provider: voiceProvider }),
    enabled: !!form,
  });

  const previewVoice = useMutation({
    mutationFn: () =>
      api.post("/provider-center/voices/preview", {
        provider: voiceProvider,
        voice_id: form.voice_id,
        language: form.language,
        speed: Number(form.speaking_speed) || 1,
        text: form.conversation?.opening_message ||
          `Hello, I am ${form.name}, an AI assistant calling from ${form.company_name}.`,
      }),
    onSuccess: ({ data }) => new Audio(data.audio_url).play(),
    onError: (e) => toast.error(apiError(e)),
  });

  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setConv = (key, value) => setForm((f) => ({ ...f, conversation: { ...f.conversation, [key]: value } }));
  const insertVariable = (key, ref, token) => {
    const current = form.conversation?.[key] || "";
    const el = ref.current;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? start;
    setConv(key, `${current.slice(0, start)}${token}${current.slice(end)}`);
    const pos = start + token.length;
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(pos, pos);
    });
  };

  const linkAssistant = useMutation({
    mutationFn: () => api.post(`/agents/${id}/link-assistant`, { assistant_id: assistantId }),
    onSuccess: ({ data }) => {
      setForm((f) => ({ ...f, conversation: data.conversation, voice_agent_provider: "vapi" }));
      setLinkOpen(false);
      setAssistantId("");
      toast.success(`Loaded the system prompt from ${data.remote_name || data.provider_resource_id}`);
      queryClient.invalidateQueries({ queryKey: ["agent", id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const refresh = async () => {
    const fresh = await queryClient.fetchQuery({
      queryKey: ["agent", id], queryFn: () => get(`/agents/${id}`),
    });
    setForm(fresh);
  };

  const publish = useMutation({
    mutationFn: async () => {
      await api.put(`/agents/${id}`, {
        name: form.name, company_name: form.company_name, role: form.role, language: form.language,
        voice_agent_provider: form.voice_agent_provider,
        country: form.country, product: form.product, voice_provider: form.voice_provider,
        voice_id: form.voice_id, speaking_speed: form.speaking_speed, tone: form.tone,
        energy_level: form.energy_level, formality_level: form.formality_level,
        persuasion_level: form.persuasion_level, model: form.model, temperature: form.temperature,
        conversation: form.conversation || {}, knowledge: form.knowledge || {},
        max_call_duration: form.max_call_duration,
        phone_number_id: form.phone_number_id || null,
      });
      const { data } = await api.post(`/agents/${id}/publish`, { change_notes: notes });
      return data;
    },
    onSuccess: (version) => {
      const sync = version.sync;
      if (sync && sync.sync_status && sync.sync_status !== "synced") {
        toast.error(sync.error?.message || sync.error?.reason
          || "Published locally, but Vapi rejected the assistant");
      } else {
        toast.success(`Published version ${version.version}`);
      }
      setPublishOpen(false);
      setNotes("");
      refresh();    },
    onError: (e) => toast.error(apiError(e)),
  });

  const restore = useMutation({
    mutationFn: (version) => api.post(`/agents/${id}/versions/${version}/restore`, {}),
    onSuccess: () => {
      toast.success("Version restored into the draft");
      refresh();    },
    onError: (e) => toast.error(apiError(e)),
  });

  const runCompare = useMutation({
    mutationFn: () => get(`/agents/${id}/versions/compare`, { a: compare.a, b: compare.b }),
    onSuccess: (result) => setDiff(result),
    onError: (e) => toast.error(apiError(e)),
  });

  const syncAgent = useMutation({
    mutationFn: () => api.post(`/agents/${id}/sync`, {}),
    onSuccess: ({ data: r }) => {
      if (r.sync_status === "synced") toast.success(`Synchronised with ${r.provider}`);
      else toast.error(r.error?.reason || r.error?.message || "Synchronisation failed");
      refresh();    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remoteDiff = useMutation({
    mutationFn: () => get(`/agents/${id}/remote-diff`),
    onSuccess: (r) => {
      setDiff({ version_a: "local", version_b: "remote", differences: r.differences || [] });
      toast.success(`${(r.differences || []).length} field differences · ${r.sync_status}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const disableRemote = useMutation({
    mutationFn: () => api.post(`/agents/${id}/disable-remote`, {}),
    onSuccess: () => { toast.success("Remote agent disabled"); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail?.reason || apiError(e)),
  });

  const test = useMutation({
    mutationFn: ({ live, phone }) =>
      api.post(`/agents/${id}/test`, {
        mode: phone ? "phone" : "browser",
        live: !!live,
        ...(phone ? { phone } : {}),
      }),
    onSuccess: ({ data: result }) => {
      setTestResult(result);
      if (result.live) {
        toast.success(
          result.provider_call_id
            ? `Live call started on ${result.provider} · call ${String(result.provider_call_id).slice(0, 8)}…`
            : `Live test accepted by ${result.provider}`
        );
      }
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.reason || e.response?.data?.detail?.message || apiError(e)),
  });

  const addDoc = useMutation({
    mutationFn: () => api.post(`/agents/${id}/documents`, { ...newDoc, type: "text" }),
    onSuccess: () => {
      toast.success("Knowledge document added");
      setNewDoc({ title: "", content: "" });
      refresh();    },
    onError: (e) => toast.error(apiError(e)),
  });

  const removeDoc = useMutation({
    mutationFn: (docId) => api.delete(`/agents/${id}/documents/${docId}`),
    onSuccess: () => {
      refresh();    },
  });

  if (!form) return <p className="text-sm text-slate-500">Loading agent…</p>;
  const editable = can("agents", "write");

  return (
    <>
      <PageHeader
        testId="agent-detail-page"
        title={form.name}
        subtitle={`${form.company_name} · ${(form.language || "").toUpperCase()} · ${form.country} · ${data.published_version ? `published v${data.published_version}` : "not published"}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <StatusBadge value={data.draft_dirty ? "draft" : "published"} label={data.draft_dirty ? "Unpublished changes" : "Published"} testId="agent-status-badge" />
            <SyncBadge status={data.sync_status || "not_connected"} testId="agent-sync-badge" />
            {editable ? (
              <>
                <Button variant="outline" size="sm" onClick={() => syncAgent.mutate()} data-testid="agent-sync-button">Synchronise</Button>
                <Button variant="outline" size="sm" onClick={() => remoteDiff.mutate()} data-testid="agent-remote-diff-button">Remote diff</Button>
                {form.provider_resource_id ? (
                  <Button variant="outline" size="sm" className="text-rose-600" onClick={() => disableRemote.mutate()} data-testid="agent-disable-remote">Disable remote</Button>
                ) : null}
              </>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => test.mutate({ live: false })} data-testid="agent-test-button">
              <Play className="mr-1 h-3.5 w-3.5" /> Test version
            </Button>
            {editable ? (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setPublishOpen(true)} data-testid="agent-publish-button">
                Publish version
              </Button>
            ) : null}
          </div>
        }
      />

      <Tabs defaultValue="general">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="general" data-testid="agent-tab-general">General & voice</TabsTrigger>
          <TabsTrigger value="conversation" data-testid="agent-tab-conversation">{CONVERSATION_TAB_LABEL}</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="agent-tab-knowledge">Knowledge base</TabsTrigger>
          <TabsTrigger value="versions" data-testid="agent-tab-versions">Versions</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Section title="General configuration">
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Agent name</Label>
                <Input data-testid="agent-field-name" value={form.name} disabled={!editable} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Company name</Label>
                <Input data-testid="agent-field-company" value={form.company_name} disabled={!editable} onChange={(e) => set({ company_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Input data-testid="agent-field-role" value={form.role || ""} disabled={!editable} onChange={(e) => set({ role: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Product</Label>
                <Input data-testid="agent-field-product" value={form.product || ""} disabled={!editable} onChange={(e) => set({ product: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Select value={form.language} disabled={!editable} onValueChange={(v) => set({ language: v })}>
                  <SelectTrigger data-testid="agent-field-language"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Outbound number</Label>
                <Select value={form.phone_number_id || "none"} disabled={!editable} onValueChange={(v) => set({ phone_number_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="agent-field-number">
                    <SelectValue placeholder="No number assigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">No number assigned</SelectItem>
                    {pickableNumbers.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.phone} · {n.country}{n.voice_provider_resource_id ? " · in Vapi" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  This agent calls only from this number. A number used by another agent is rejected.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Default country</Label>
                <Select value={form.country} disabled={!editable} onValueChange={(v) => set({ country: v })}>
                  <SelectTrigger data-testid="agent-field-country"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Voice agent provider</Label>
                <Select value={form.voice_agent_provider || "vapi"} disabled={!editable} onValueChange={(v) => set({ voice_agent_provider: v })}>
                  <SelectTrigger data-testid="agent-field-voice-agent-provider"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="vapi">Vapi (live calls)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  {form.voice_agent_provider === "vapi"
                    ? "Publishing creates or updates a real Vapi assistant."
                    : "Simulation only — switch to Vapi before running live calls."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Voice provider</Label>
                <Select value={voiceProvider} disabled={!editable} onValueChange={(v) => set({ voice_provider: v, voice_id: "" })}>
                  <SelectTrigger data-testid="agent-field-voice-provider"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="vapi">Vapi (built-in voices)</SelectItem>
                    <SelectItem value="elevenlabs">ElevenLabs (premium, multilingual)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Voice</Label>
                <Select value={form.voice_id || ""} disabled={!editable} onValueChange={(v) => set({ voice_id: v })}>
                  <SelectTrigger data-testid="agent-field-voice"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(voices || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tone</Label>
                <Input data-testid="agent-field-tone" value={form.tone || ""} disabled={!editable} onChange={(e) => set({ tone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Model</Label>
                <Input data-testid="agent-field-model" value={form.model || ""} disabled={!editable} onChange={(e) => set({ model: e.target.value })} />
              </div>
              {[
                ["speaking_speed", "Speaking speed", 0.5, 1.5, 0.05],
                ["energy_level", "Energy level", 1, 5, 1],
                ["formality_level", "Formality level", 1, 5, 1],
                ["persuasion_level", "Persuasion level", 1, 5, 1],
              ].map(([key, label, min, max, step]) => (
                <div key={key} className="space-y-2">
                  <Label>
                    {label}: <span className="font-mono text-xs text-slate-500">{form[key]}</span>
                  </Label>
                  <Slider
                    data-testid={`agent-slider-${key}`}
                    disabled={!editable}
                    min={min}
                    max={max}
                    step={step}
                    value={[Number(form[key]) || min]}
                    onValueChange={([v]) => set({ [key]: v })}
                  />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label>Max call duration (seconds)</Label>
                <Input
                  data-testid="agent-field-max-duration"
                  type="number"
                  value={form.max_call_duration || 300}
                  disabled={!editable}
                  onChange={(e) => set({ max_call_duration: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Voice preview</Label>
                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="agent-voice-preview"
                  disabled={!form.voice_id || previewVoice.isPending}
                  onClick={() => {
                    if (voiceProvider !== "elevenlabs") {
                      toast.info("Previews are available for ElevenLabs voices. Vapi voices are heard on a test call.");
                      return;
                    }
                    previewVoice.mutate();
                  }}
                >
                  {previewVoice.isPending ? "Generating…" : "Preview voice"}
                </Button>
              </div>
            </div>
          </Section>

          <Section
            title="Test this agent"
            subtitle={form.voice_agent_provider === "vapi"
              ? "Live calls use the published version, the Vapi assistant and an imported phone number"
              : "Set the voice agent provider to Vapi and publish before a real call"}
          >
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_auto_auto]">
              <Input
                data-testid="agent-live-test-phone"
                value={livePhone}
                placeholder="+39 333 111 0001 — your own number in E.164"
                onChange={(e) => setLivePhone(e.target.value)}
              />
              <Button
                variant="outline"
                data-testid="agent-simulate-button"
                disabled={test.isPending}
                onClick={() => test.mutate({ live: false })}
              >
                Simulate conversation
              </Button>
              <Button
                className="bg-gradient-to-r from-indigo-600 to-violet-600 font-semibold text-white hover:from-indigo-700 hover:to-violet-700"
                data-testid="agent-live-call-button"
                disabled={!editable || !livePhone.trim() || test.isPending}
                onClick={() => test.mutate({ live: true, phone: livePhone.trim() })}
              >
                {test.isPending ? "Dialling…" : "Call my phone (live)"}
              </Button>
            </div>
            {form.voice_agent_provider !== "vapi" ? (
              <p className="px-4 pb-4 text-xs text-amber-700" data-testid="agent-live-call-warning">
                A live call needs: voice agent provider = Vapi, a published version, Vapi
                credentials and a phone number imported into Vapi.
              </p>
            ) : null}
          </Section>
        </TabsContent>

        <TabsContent value="conversation" className="mt-4">
          <Section
            title="System prompt"
            subtitle="The only prompt pushed to Vapi. Voice, model, speaking plans and timeouts stay in the Vapi dashboard."
            actions={can("agents", "write") ? (
              <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)} data-testid="agent-link-assistant-btn">
                Load from Vapi assistant
              </Button>
            ) : null}
          >
            <div className="space-y-4 p-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>First message</Label>
                  <VariableMenu
                    disabled={!editable}
                    testId="agent-insert-first-message"
                    onInsert={(token) => insertVariable("first_message", firstMessageRef, token)}
                  />
                </div>
                <Textarea
                  ref={firstMessageRef}
                  data-testid="agent-conv-first_message"
                  rows={2}
                  disabled={!editable}
                  value={form.conversation?.first_message || ""}
                  onChange={(e) => setConv("first_message", e.target.value)}
                  placeholder="Hello {{firstname}} how are you?"
                />
                <p className="text-xs text-slate-500">
                  <code>{"{{firstname}}"}</code> is replaced with the first name of the contact being called.
                </p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>System prompt</Label>
                  <VariableMenu
                    disabled={!editable}
                    testId="agent-insert-system-prompt"
                    onInsert={(token) => insertVariable("system_prompt", promptRef, token)}
                  />
                </div>
                <Textarea
                  ref={promptRef}
                  data-testid="agent-conv-system_prompt"
                  rows={20}
                  className="font-mono text-xs"
                  disabled={!editable}
                  value={form.conversation?.system_prompt || ""}
                  onChange={(e) => setConv("system_prompt", e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Saved as the agent script and pushed to the assistant as a single
                  {" "}<code>system</code> message on publish and sync. Contact tokens such as
                  {" "}<code>{"{{firstname}}"}</code> stay in the script and are filled on each call.
                </p>
              </div>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="knowledge" className="mt-4 space-y-4">
          <Section title="Product knowledge">
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Products, prices and packages (JSON)</Label>
                <Textarea
                  data-testid="agent-knowledge-products"
                  rows={8}
                  disabled={!editable}
                  value={JSON.stringify(form.knowledge?.products || [], null, 2)}
                  onChange={(e) => {
                    try {
                      set({ knowledge: { ...form.knowledge, products: JSON.parse(e.target.value) } });
                    } catch {
                      /* keep typing */
                    }
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Objections and approved responses (JSON)</Label>
                <Textarea
                  data-testid="agent-knowledge-objections"
                  rows={8}
                  disabled={!editable}
                  value={JSON.stringify(form.knowledge?.objections || [], null, 2)}
                  onChange={(e) => {
                    try {
                      set({ knowledge: { ...form.knowledge, objections: JSON.parse(e.target.value) } });
                    } catch {
                      /* keep typing */
                    }
                  }}
                />
              </div>
            </div>
          </Section>

          <Section title="Knowledge documents">
            <DataTable
              testId="agent-documents-table"
              columns={[
                { key: "title", label: "Title" },
                { key: "type", label: "Type", render: (r) => title(r.type) },
                { key: "content", label: "Content", render: (r) => <span className="line-clamp-1 text-slate-600">{r.content}</span> },
                {
                  key: "actions",
                  label: "",
                  render: (r) =>
                    editable ? (
                      <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => removeDoc.mutate(r.id)} data-testid={`agent-doc-delete-${r.id}`}>
                        Remove
                      </Button>
                    ) : null,
                },
              ]}
              rows={data.documents || []}
              empty={{ title: "No documents added" }}
            />
            {editable ? (
              <div className="grid grid-cols-1 gap-3 border-t border-slate-200 p-4 md:grid-cols-[1fr_2fr_auto]">
                <Input data-testid="agent-doc-title" placeholder="Document title" value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} />
                <Input data-testid="agent-doc-content" placeholder="Content, transcript or FAQ text" value={newDoc.content} onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })} />
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!newDoc.title || !newDoc.content}
                  onClick={() => addDoc.mutate()}
                  data-testid="agent-doc-add"
                >
                  Add document
                </Button>
              </div>
            ) : null}
          </Section>
        </TabsContent>

        <TabsContent value="versions" className="mt-4 space-y-4">
          <Section title="Version history" subtitle="Published versions are immutable">
            <DataTable
              testId="agent-versions-table"
              columns={[
                { key: "version", label: "Version", render: (r) => `v${r.version}` },
                { key: "change_notes", label: "Change notes" },
                { key: "created_by_name", label: "Created by", render: (r) => r.created_by_name || "—" },
                { key: "created_at", label: "Created", render: (r) => dt(r.created_at) },
                { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                {
                  key: "actions",
                  label: "",
                  render: (r) =>
                    editable ? (
                      <Button variant="ghost" size="sm" onClick={() => restore.mutate(r.version)} data-testid={`agent-restore-${r.version}`}>
                        Restore as draft
                      </Button>
                    ) : null,
                },
              ]}
              rows={data.versions || []}
              empty={{ title: "No published versions yet" }}
            />
          </Section>

          <Section title="Compare versions">
            <div className="flex flex-wrap items-end gap-3 p-4">
              {["a", "b"].map((side) => (
                <div key={side} className="space-y-1.5">
                  <Label>Version {side.toUpperCase()}</Label>
                  <Select value={compare[side]} onValueChange={(v) => setCompare((c) => ({ ...c, [side]: v }))}>
                    <SelectTrigger data-testid={`agent-compare-${side}`} className="w-40"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {(data.versions || []).map((v) => (
                        <SelectItem key={v.version} value={String(v.version)}>v{v.version}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <Button
                variant="outline"
                disabled={!compare.a || !compare.b}
                onClick={() => runCompare.mutate()}
                data-testid="agent-compare-run"
              >
                Compare
              </Button>
            </div>
            {diff ? (
              <div className="border-t border-slate-200" data-testid="agent-diff-result">
                <DataTable
                  columns={[
                    { key: "field", label: "Field", render: (r) => <span className="font-mono text-xs">{r.field}</span> },
                    { key: "old", label: `v${diff.version_a}`, render: (r) => <span className="text-rose-700">{JSON.stringify(r.old)}</span> },
                    { key: "new", label: `v${diff.version_b}`, render: (r) => <span className="text-emerald-700">{JSON.stringify(r.new)}</span> },
                  ]}
                  rows={(diff.differences || []).map((d, i) => ({ ...d, id: i }))}
                  empty={{ title: "The two versions are identical" }}
                />
              </div>
            ) : null}
          </Section>
        </TabsContent>
      </Tabs>

      {testResult ? (
        <Section title={testResult.live ? "Live test call" : "Test conversation"} testId="agent-test-result">
          <div className="space-y-2 p-4">
            {testResult.live ? (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900" data-testid="agent-live-test-result">
                Provider: <span className="font-semibold">{testResult.provider}</span>
                {testResult.provider_call_id ? (
                  <> · call id <span className="font-mono text-xs">{testResult.provider_call_id}</span></>
                ) : null}
                {" "}— the transcript and cost appear in Calls once the provider reports back.
              </div>
            ) : null}
            {(testResult.transcript || []).map((seg, i) => (
              <div key={i} className={cn("flex", seg.speaker === "customer" ? "justify-end" : "justify-start")}>
                <p className={cn("max-w-lg rounded-lg px-3 py-1.5 text-sm",
                  seg.speaker === "customer" ? "bg-slate-100 text-slate-700" : "bg-indigo-50 text-indigo-900")}>
                  {seg.text}
                </p>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      <Link to="/agents" className="text-sm text-blue-600 hover:underline" data-testid="back-to-agents">
        ← Back to agents
      </Link>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Publish new version</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Change notes</Label>
            <Textarea data-testid="publish-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changed in this version?" />
          </div>
          <DialogFooter>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={publish.isPending}
              onClick={() => publish.mutate()}
              data-testid="publish-submit"
            >
              Publish immutable version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Load the system prompt from Vapi</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Vapi assistant ID</Label>
            <Input
              data-testid="agent-link-assistant-input"
              value={assistantId}
              onChange={(e) => setAssistantId(e.target.value)}
              placeholder="e.g. 7f3c1e2a-..."
            />
            <p className="text-xs text-slate-500">
              The assistant's current system message and first message are copied into this editor.
              Later saves here are pushed back to that assistant.
            </p>
          </div>
          <DialogFooter>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!assistantId.trim() || linkAssistant.isPending}
              onClick={() => linkAssistant.mutate()}
              data-testid="agent-link-assistant-submit"
            >
              {linkAssistant.isPending ? "Loading…" : "Load prompt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}